import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const CreditCards = () => {
  const [cards, setCards] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardholderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    billingAddress: '',
    isDefault: false
  });
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = user.id;

  useEffect(() => {
    if (!userId) {
      navigate('/unauthorized');
      return;
    }
  fetchCards();
  }, [userId, navigate]);

  const fetchCards = async () => {
    try {
  const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/credit-cards?user_id=${userId}`);
      const data = await res.json();
      if (res.ok) {
        setCards(data.cards || []);
      }
    } catch (err) {
      console.error('Error fetching cards:', err);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      const cleanValue = value.replace(/\s/g, '');
      let formatted = '';
      let maxLength = 16;
      
      // Check if it's American Express (starts with 3)
      if (cleanValue.startsWith('3')) {
        maxLength = 15;
        // Format American Express: 4-6-5 (xxxx xxxxxx xxxxx)
        if (cleanValue.length <= 4) {
          formatted = cleanValue;
        } else if (cleanValue.length <= 10) {
          formatted = cleanValue.slice(0, 4) + ' ' + cleanValue.slice(4);
        } else {
          formatted = cleanValue.slice(0, 4) + ' ' + cleanValue.slice(4, 10) + ' ' + cleanValue.slice(10);
        }
      } else {
        // Format other cards: 4-4-4-4 (xxxx xxxx xxxx xxxx)
        formatted = cleanValue.replace(/(.{4})/g, '$1 ').trim();
      }
      
      if (cleanValue.length <= maxLength) {
        setFormData(prev => ({ ...prev, [name]: formatted }));
      }
      return;
    }
    
    // Format CVV - American Express has 4 digits, others have 3
    if (name === 'cvv') {
      const cardNumber = formData.cardNumber.replace(/\s/g, '');
      const maxCvvLength = cardNumber.startsWith('3') ? 4 : 3;
      if (value.length > maxCvvLength) return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    // Validation
    const cardNumberClean = formData.cardNumber.replace(/\s/g, '');
    const isAmex = cardNumberClean.startsWith('3');
    const expectedLength = isAmex ? 15 : 16;
    
    if (cardNumberClean.length !== expectedLength) {
      setMessage(`Card number must be ${expectedLength} digits for ${isAmex ? 'American Express' : 'this card type'}`);
      return;
    }
    
    if (!formData.cardholderName.trim()) {
      setMessage('Cardholder name is required');
      return;
    }
    
    if (!formData.expiryMonth || !formData.expiryYear) {
      setMessage('Expiry date is required');
      return;
    }
    
    const expectedCvvLength = isAmex ? 4 : 3;
    if (formData.cvv.length !== expectedCvvLength) {
      setMessage(`CVV must be ${expectedCvvLength} digits for ${isAmex ? 'American Express' : 'this card type'}`);
      return;
    }

    try {
  const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/credit-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          cardNumber: cardNumberClean,
          user_id: userId
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        setMessage('Credit card saved successfully!');
        setShowForm(false);
        setFormData({
          cardNumber: '',
          cardholderName: '',
          expiryMonth: '',
          expiryYear: '',
          cvv: '',
          billingAddress: '',
          isDefault: false
        });
        fetchCards();
      } else {
        setMessage(data.message || 'Error saving card');
      }
    } catch (err) {
      setMessage('Error saving card');
    }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    
    try {
  const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/credit-cards/${cardId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        setMessage('Card deleted successfully');
        fetchCards();
      } else {
        setMessage('Error deleting card');
      }
    } catch (err) {
      setMessage('Error deleting card');
    }
  };

  const handleSetDefault = async (cardId) => {
    try {
  const res = await fetch(`${process.env.REACT_APP_SERVICE_DOMAIN}/api/credit-cards/${cardId}/default`, {
        method: 'PUT'
      });
      
      if (res.ok) {
        setMessage('Default card updated');
        fetchCards();
      } else {
        setMessage('Error updating default card');
      }
    } catch (err) {
      setMessage('Error updating default card');
    }
  };

  const maskCardNumber = (cardNumber) => {
    if (!cardNumber) return '';
    
    // Check if it's American Express (15 digits starting with 3)
    if (cardNumber.startsWith('3') && cardNumber.length === 15) {
      const lastFour = cardNumber.slice(-4);
      return `**** ****** *${lastFour}`;
    } else {
      // Standard 16-digit card
      const lastFour = cardNumber.slice(-4);
      return `**** **** **** ${lastFour}`;
    }
  };

  const getCardBrand = (cardNumber) => {
    if (!cardNumber) return '';
    const firstDigit = cardNumber.charAt(0);
    const firstTwo = cardNumber.substring(0, 2);
    
    if (firstDigit === '4') return 'Visa';
    if (firstDigit === '5' || (parseInt(firstTwo) >= 51 && parseInt(firstTwo) <= 55)) return 'Mastercard';
    if (firstDigit === '3' && (cardNumber.startsWith('34') || cardNumber.startsWith('37'))) return 'American Express';
    if (firstDigit === '6') return 'Discover';
    return 'Unknown';
  };

  // Update the form input placeholder and help text
  const getCardNumberPlaceholder = () => {
    const cardNumber = formData.cardNumber.replace(/\s/g, '');
    if (cardNumber.startsWith('3')) {
      return '3456 789012 34567'; // American Express format
    }
    return '1234 5678 9012 3456'; // Standard format
  };

  const getCvvPlaceholder = () => {
    const cardNumber = formData.cardNumber.replace(/\s/g, '');
    if (cardNumber.startsWith('3')) {
      return '1234'; // American Express CVV
    }
    return '123'; // Standard CVV
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Credit Cards</h2>
      
      {message && (
        <div style={{
          ...styles.message,
          color: message.includes('Error') || message.includes('error') ? '#d32f2f' : '#388e3c'
        }}>
          {message}
        </div>
      )}

      <div style={styles.header}>
        <button
          style={styles.addBtn}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Add New Card'}
        </button>
      </div>

      {showForm && (
        <div style={styles.formContainer}>
          <h3 style={styles.formTitle}>Add New Credit Card</h3>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Card Number
                  {formData.cardNumber.replace(/\s/g, '').startsWith('3') && (
                    <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '8px' }}>
                      (American Express - 15 digits)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={handleInputChange}
                  placeholder={getCardNumberPlaceholder()}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Cardholder Name</label>
                <input
                  type="text"
                  name="cardholderName"
                  value={formData.cardholderName}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  style={styles.input}
                  required
                />
              </div>
            </div>
            
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Expiry Month</label>
                <select
                  name="expiryMonth"
                  value={formData.expiryMonth}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  <option value="">Month</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {String(i + 1).padStart(2, '0')}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Expiry Year</label>
                <select
                  name="expiryYear"
                  value={formData.expiryYear}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  <option value="">Year</option>
                  {Array.from({ length: 15 }, (_, i) => (
                    <option key={i} value={new Date().getFullYear() + i}>
                      {new Date().getFullYear() + i}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  CVV
                  {formData.cardNumber.replace(/\s/g, '').startsWith('3') && (
                    <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '8px' }}>
                      (4 digits for American Express)
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleInputChange}
                  placeholder={getCvvPlaceholder()}
                  style={styles.input}
                  required
                />
              </div>
            </div>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Billing Address</label>
              <textarea
                name="billingAddress"
                value={formData.billingAddress}
                onChange={handleInputChange}
                placeholder="123 Main St, City, State, ZIP"
                style={styles.textarea}
                rows={3}
              />
            </div>
            
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleInputChange}
                  style={styles.checkbox}
                />
                Set as default payment method
              </label>
            </div>
            
            <button type="submit" style={styles.submitBtn}>
              Save Card
            </button>
          </form>
        </div>
      )}

      <div style={styles.cardsContainer}>
        {cards.length === 0 ? (
          <div style={styles.empty}>No credit cards saved</div>
        ) : (
          cards.map(card => (
            <div key={card.id} style={styles.cardItem}>
              <div style={styles.cardInfo}>
                <div style={styles.cardBrand}>
                  {getCardBrand(card.card_number)} {card.is_default && <span style={styles.defaultBadge}>Default</span>}
                </div>
                <div style={styles.cardNumber}>
                  {maskCardNumber(card.card_number)}
                </div>
                <div style={styles.cardDetails}>
                  <span>{card.cardholder_name}</span>
                  <span>{card.expiry_month}/{card.expiry_year}</span>
                </div>
                {card.billing_address && (
                  <div style={styles.address}>{card.billing_address}</div>
                )}
              </div>
              <div style={styles.cardActions}>
                {!card.is_default && (
                  <button
                    style={styles.defaultBtn}
                    onClick={() => handleSetDefault(card.id)}
                  >
                    Set Default
                  </button>
                )}
                <button
                  style={styles.deleteBtn}
                  onClick={() => handleDelete(card.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <button
        style={styles.backBtn}
        onClick={() => navigate('/customerdashboard')}
      >
        ← Back to Dashboard
      </button>
      
      <button
        style={styles.paymentBtn}
        onClick={() => navigate('/invoices')} // Go back to previous page (payment page)
      >
        ← Back to Invoices
      </button>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    background: '#e3f2fd', // Light blue background
    minHeight: '100vh',
    borderRadius: '8px',
  },
  title: {
    textAlign: 'center',
    color: '#1976d2', // Darker blue for better contrast
    marginBottom: '30px',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    fontSize: '1.2rem',
    marginTop: '50px',
  },
  message: {
    textAlign: 'center',
    padding: '10px',
    marginBottom: '20px',
    borderRadius: '4px',
    fontWeight: 'bold',
    background: '#fff',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '20px',
  },
  addBtn: {
    background: '#1976d2', // Darker blue for better contrast
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '1rem',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  formContainer: {
    background: '#f8f9fa', // Slightly different from main background
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '30px',
    border: '1px solid #ddd',
  },
  formTitle: {
    color: '#1976d2', // Darker blue for better contrast
    marginBottom: '20px',
    fontSize: '1.3rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  row: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap',
  },
  inputGroup: {
    flex: '1',
    minWidth: '200px',
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  checkboxGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#333',
  },
  checkbox: {
    marginRight: '10px',
    transform: 'scale(1.2)',
  },
  submitBtn: {
    background: '#388e3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 24px',
    fontSize: '1rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    alignSelf: 'flex-start',
  },
  cardsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  empty: {
    textAlign: 'center',
    color: '#666',
    fontSize: '1.1rem',
    padding: '40px',
    fontStyle: 'italic',
    background: '#fff',
    borderRadius: '8px',
  },
  cardItem: {
    background: '#fff',
    border: '1px solid #bbdefb', // Light blue border to match theme
    borderRadius: '8px',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.1)', // Blue-tinted shadow
  },
  cardInfo: {
    flex: '1',
  },
  cardBrand: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
    color: '#1976d2', // Darker blue for better contrast
    marginBottom: '5px',
  },
  defaultBadge: {
    background: '#388e3c',
    color: '#fff',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    marginLeft: '10px',
  },
  cardNumber: {
    fontSize: '1.2rem',
    letterSpacing: '2px',
    marginBottom: '10px',
    fontFamily: 'monospace',
  },
  cardDetails: {
    display: 'flex',
    gap: '20px',
    color: '#666',
    fontSize: '0.9rem',
  },
  address: {
    color: '#666',
    fontSize: '0.9rem',
    marginTop: '10px',
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  defaultBtn: {
    background: '#1976d2', // Darker blue for better contrast
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  deleteBtn: {
    background: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  backBtn: {
    background: '#1976d2', // Darker blue for better contrast
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(25, 118, 210, 0.3)',
    transition: 'background 0.2s',
    textDecoration: 'none',
    marginTop: '32px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  paymentBtn: {
    background: '#388e3c',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px #c8e6c9',
    transition: 'background 0.2s',
    textDecoration: 'none',
    marginTop: '16px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
};

export default CreditCards;